import { JiraIssueData } from '../../../api/endpoints/jira';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { PodRecordSet, RecordSetManager } from '../../../concepts/objects/RecordSetManager';
import { EditorOptions } from '../../canvas/EditorOptions';
import { ActiveObject, resolve } from '../../Container';
import { BoardSetting } from '../boardSettings/BoardSetting';
import { jiraCardFixSizes, jiraLoadInitial, jiraLoadMissing } from './AtlassianCommands';
import { AtlassianContext } from './AtlassianContext';
import { BindingManager } from './BindingManager';
import { JiraCard, JiraCardCustomsVisibleAttr, JiraCardDataRecordAttr } from './JiraCard';
import { PodRecordBindingTarget } from './PodRecordBindingTarget';

// Pretty sure this can be generalized in the future - but do that when we need to...

export class JiraCardMechanics extends ActiveObject {

    @resolve(AtlassianContext) private atlassian: AtlassianContext;
    @resolve(BindingManager) private bindings: BindingManager;
    @resolve(CommandDispatcher) private dispatch: CommandDispatcher;
    @resolve(ObjectManager) private objects: ObjectManager;
    @resolve(EditorOptions) private options: EditorOptions;
    @resolve(RecordSetManager) private recordSets: RecordSetManager;

    private recordSet: PodRecordSet<JiraIssueData>;

    public start() {

        if (this.options.kiosk) {
            return;
        }

        this.burden(
            this.objects.on('JiraCard', 'allocated', ([x]) => this.configureBinding(x.as(JiraCard))),
            this.objects.on('JiraCard', 'affected', ([x]) => this.configureBinding(x.as(JiraCard))),
            this.objects.on('JiraCard', 'released', ([x]) => this.releaseBinding(x.as(JiraCard))),
            this.objects.createTrigger('JiraCard.Load')
                .for(JiraCard)
                .onChangesTo('externalId')
                .doForEach(this.loadJiraRecordIfRequired),

            this.objects.on('JiraCard', 'allocated', () => this.syncCustomFieldVisibility()),
            this.objects.on('BoardSetting', 'allocated', () => this.syncCustomFieldVisibility()),
            this.objects.on('BoardSetting', 'affected', () => this.syncCustomFieldVisibility()),
            this.objects.on('BoardSetting', 'released', () => this.syncCustomFieldVisibility()),
            this.objects.createTrigger('JiraCard.SizeToContentOnSettingChange')
                .for(BoardSetting)
                .onChangesTo('value')
                .doForEach(x => this.sizeAllCardsToContentOnFieldVisibilityChange(x)),
        );

        this.recordSet = this.recordSets.resolve('JiraIssues');
        this.configureInitialBindings();
        this.dispatch(jiraLoadInitial);
        this.syncCustomFieldVisibility();

        this.scheduler.timeout('loadFuture', 1000, () => {
            this.dispatch(jiraLoadMissing);
        });
    }

    private loadJiraRecordIfRequired = (card: JiraCard) => {
        if (!card.externalId) return;
        if (!!JiraCardDataRecordAttr.get(card)?.created) return;
        this.atlassian.queueIssueLoad(card.externalId);
    };

    private configureInitialBindings() {
        const allCards = Array.from(this.objects.getAll(JiraCard));
        for (const card of allCards) {
            this.configureBinding(card);
        }
    }

    private configureBinding(card: JiraCard) {
        if (!card.externalId) return; // Can't bind
        const key = `record:${card.externalId}`;
        if (this.bindings.check(card, key)) return;
        const record = this.recordSet.getForFuture(card.externalId);
        this.bindings.bind(card, new PodRecordBindingTarget(key, record), JiraCardDataRecordAttr);
    }

    private releaseBinding(card: JiraCard) {
        this.bindings.unbind(card);
    }

    private syncCustomFieldVisibility() {

        const values = {
            'customfield_10016': true,
            'customfield_10020': true,
            ...BoardSetting.readGroup('jira', this.objects),
        };

        const names = Object.entries(values).filter(([_k, v]) => !!v).map(([k]) => k);
        const cards = Array.from(this.objects.getAll(JiraCard));

        for (const c of cards) {
            JiraCardCustomsVisibleAttr.set(c, names);
        }

    }

    private sizeAllCardsToContentOnFieldVisibilityChange(setting: BoardSetting) {
        if (setting.group === 'jira') {
            this.syncCustomFieldVisibility();
            this.dispatch(jiraCardFixSizes);
        }
    }
}
